import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  BookOpen, BrainCircuit,
  ChevronLeft,
  ChevronRight,
  Crosshair,
  ExternalLink,
  LayoutDashboard,
  LineChart,
  LogOut,
  Menu,
  Settings,
  User,
  Users,
  X,
} from 'lucide-react';
import Logo from '../common/Logo';
import OrgSwitcher from './OrgSwitcher';
import NotificationBell from './NotificationBell';
import SectionErrorBoundary from './SectionErrorBoundary';
import { useAuth } from '../../contexts/AuthContext';
import { buildAdminOrgUrl } from '../../lib/adminApp';

interface NavItem {
  to: string;
  icon: typeof LayoutDashboard;
  label: string;
  end: boolean;
  badge?: string;
  requiresBeta?: boolean;
  external?: boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const NAV_ITEM_CLASSES = 'app-focus-ring group relative flex min-h-11 items-center gap-3 px-3 py-2.5 text-sm font-medium transition';
const NAV_ITEM_IDLE_CLASSES = 'text-app-chrome-muted hover:bg-white/[0.06] hover:text-white';

function SidebarNavItem({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  if (item.external) {
    return (
      <a href={item.to} target="_blank" rel="noopener noreferrer" title={collapsed ? `${item.label} (opens in a new tab)` : undefined} className={`${NAV_ITEM_CLASSES} ${NAV_ITEM_IDLE_CLASSES}`}>
        <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
        {!collapsed ? <span className="flex min-w-0 items-center gap-1.5"><span className="truncate">{item.label}</span><ExternalLink className="h-3.5 w-3.5 shrink-0" aria-label="Opens in a new tab" /></span> : null}
      </a>
    );
  }

  return (
    <NavLink to={item.to} end={item.end} title={collapsed ? item.label : undefined} className={({ isActive }) => `${NAV_ITEM_CLASSES} ${isActive ? 'bg-app-accent text-app-accent-text' : NAV_ITEM_IDLE_CLASSES}`}>
      <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
      {!collapsed ? (
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate">{item.label}</span>
          {item.badge ? <span className="border border-current px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase leading-none">{item.badge}</span> : null}
        </span>
      ) : null}
    </NavLink>
  );
}

function Sidebar({ collapsed, mobile, sections, appName, logoUrl, email, onClose, onCollapse, onSignOut }: {
  collapsed: boolean;
  mobile: boolean;
  sections: NavSection[];
  appName: string;
  logoUrl?: string;
  email?: string;
  onClose: () => void;
  onCollapse: () => void;
  onSignOut: () => void;
}) {
  return (
    <div className="relative flex h-full flex-col bg-app-chrome text-white">
      <div className="flex h-16 shrink-0 items-center gap-3 border-b border-white/15 px-4">
        <Logo className="h-9 w-9 shrink-0 object-contain" src={logoUrl} alt={appName} />
        {!collapsed ? <span className="min-w-0 flex-1 truncate text-lg font-semibold">{appName}</span> : null}
        {mobile ? <button type="button" onClick={onClose} className="app-focus-ring inline-flex min-h-10 min-w-10 items-center justify-center text-app-chrome-muted hover:bg-white/[0.06] hover:text-white" aria-label="Close navigation"><X className="h-5 w-5" aria-hidden="true" /></button> : null}
      </div>

      {!collapsed ? <OrgSwitcher /> : null}

      <nav className="flex-1 overflow-y-auto px-2 py-3" aria-label="Application navigation">
        {sections.map((section, sectionIndex) => (
          <div key={section.title} className={sectionIndex ? 'mt-5 border-t border-white/15 pt-4' : ''}>
            {!collapsed ? <p className="px-3 pb-2 font-mono text-[10px] font-semibold uppercase text-app-chrome-muted">{section.title}</p> : null}
            <div className="space-y-1">{section.items.map(item => <SidebarNavItem key={item.to} item={item} collapsed={collapsed} />)}</div>
          </div>
        ))}
      </nav>

      <div className="shrink-0 border-t border-white/15 p-2">
        <NotificationBell collapsed={collapsed} />
        {!collapsed && email ? <p className="mx-1 mt-1 truncate border-l-2 border-app-accent px-3 py-2 font-mono text-[10px] text-app-chrome-muted">{email}</p> : null}
        <button type="button" onClick={onSignOut} className={`${NAV_ITEM_CLASSES} ${NAV_ITEM_IDLE_CLASSES} mt-1 w-full`}>
          <LogOut className="h-5 w-5 shrink-0" aria-hidden="true" />
          {!collapsed ? <span>Sign out</span> : null}
        </button>
        {!mobile ? (
          <button type="button" onClick={onCollapse} className={`${NAV_ITEM_CLASSES} ${NAV_ITEM_IDLE_CLASSES} mt-1 w-full`} aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'} title={collapsed ? 'Expand navigation' : undefined}>
            {collapsed ? <ChevronRight className="h-5 w-5" aria-hidden="true" /> : <ChevronLeft className="h-5 w-5" aria-hidden="true" />}
            {!collapsed ? <span>Collapse</span> : null}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function AppLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, organisation, membership, branding, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = membership?.role === 'admin';
  const betaEnabled = organisation?.beta_features_enabled ?? false;
  const appName = branding?.app_name ?? 'Heymakr';

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') setMobileOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [mobileOpen]);

  const handleSignOut = async () => { await signOut(); navigate('/'); };

  const sections: NavSection[] = [
    {
      title: 'Applications',
      items: [
        { to: '/app', icon: LayoutDashboard, label: 'Accounts', end: true },
        { to: '/app/contacts', icon: Users, label: 'Contacts', end: false, badge: 'Beta', requiresBeta: true },
        { to: '/app/executive-analytics', icon: LineChart, label: 'Executive Analytics', end: false, badge: 'Beta', requiresBeta: true },
        { to: '/app/territory-analytics', icon: BarChart3, label: 'Territory Analytics', end: false, badge: 'Beta', requiresBeta: true },
        { to: '/app/mission-control', icon: Crosshair, label: 'Mission Control', end: false, badge: 'Beta', requiresBeta: true },
        { to: '/app/mavis', icon: BrainCircuit, label: 'MAVIS', end: false, badge: 'Beta', requiresBeta: true },
        { to: '/app/learn', icon: BookOpen, label: 'Learn', end: false },
      ].filter(item => !item.requiresBeta || betaEnabled),
    },
    { title: 'Settings', items: [{ to: '/app/profile', icon: User, label: 'Profile', end: false }] },
    ...(isAdmin && organisation ? [{ title: 'Admin', items: [{ to: buildAdminOrgUrl(organisation.id), icon: Settings, label: 'Configuration', end: false, external: true }] }] : []),
  ];

  return (
    <div className="app-site flex min-h-screen">
      <a href="#app-content" className="fixed left-3 top-3 z-[100] -translate-y-20 border border-app-ink bg-app-accent px-4 py-3 text-sm font-semibold text-app-accent-text transition-transform focus:translate-y-0">Skip to content</a>

      <aside className={`fixed inset-y-0 left-0 z-40 hidden border-r border-black transition-[width] duration-200 md:block ${collapsed ? 'w-16' : 'w-64'}`}>
        <Sidebar collapsed={collapsed} mobile={false} sections={sections} appName={appName} logoUrl={branding?.logo_url} email={user?.email} onClose={() => {}} onCollapse={() => setCollapsed(value => !value)} onSignOut={handleSignOut} />
      </aside>

      {mobileOpen ? (
        <div className="fixed inset-0 z-50 md:hidden">
          <button type="button" className="absolute inset-0 bg-black/45" onClick={() => setMobileOpen(false)} aria-label="Close navigation" />
          <aside className="relative h-full w-[min(20rem,88vw)] border-r border-black shadow-2xl">
            <Sidebar collapsed={false} mobile sections={sections} appName={appName} logoUrl={branding?.logo_url} email={user?.email} onClose={() => setMobileOpen(false)} onCollapse={() => {}} onSignOut={handleSignOut} />
          </aside>
        </div>
      ) : null}

      <div className={`min-w-0 flex-1 transition-[margin] duration-200 ${collapsed ? 'md:ml-16' : 'md:ml-64'}`}>
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-app-line bg-app-surface/95 px-3 backdrop-blur md:hidden">
          <button type="button" onClick={() => setMobileOpen(true)} className="app-focus-ring inline-flex min-h-10 min-w-10 items-center justify-center border border-app-line-strong text-app-ink" aria-label="Open navigation"><Menu className="h-5 w-5" aria-hidden="true" /></button>
          <div className="flex min-w-0 items-center gap-2"><Logo className="h-8 w-8 shrink-0 object-contain" src={branding?.logo_url} alt={appName} /><span className="truncate text-sm font-semibold text-app-ink">{appName}</span></div>
          <NotificationBell collapsed />
        </header>
        <main id="app-content" className="min-w-0" tabIndex={-1}><SectionErrorBoundary resetKey={location.pathname}><Outlet /></SectionErrorBoundary></main>
      </div>
    </div>
  );
}
